'use client'
import Tool from "@/app/components/auctor/tool/Tool";
import { getTool } from "@/app/endpoints/auctorEndpoints";
import { IFilterConfig } from "@/app/models/auctor";
import { useEffect, useState } from "react";
import {TOOL_ANCHOR_CLASS, toolAnchorId} from "@/app/utils/tool-anchor";

interface ToolEmbedProps {
    toolId: string;
    title?: string;
    showFiltersAbove?: boolean;
    maxFiltersMobile?: number;
}

export default function ToolEmbed({
    toolId,
    title,
    showFiltersAbove = false,
    maxFiltersMobile,
}: ToolEmbedProps) {
    const [template, setTemplate] = useState<any>(null);
    const [filters, setFilters] = useState<IFilterConfig[]>([]);
    const [toolData, setToolData] = useState<any>(null);
    const [expressionContext, setExpressionContext] = useState<string>('');
    const [expressionOnLoad, setExpressionOnLoad] = useState<string>('');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const loadToolData = async () => {
            try {
                setIsLoading(true);
                setError(null);

                const toolIdNum = parseInt(toolId);
                if (isNaN(toolIdNum)) {
                    throw new Error(`Invalid tool ID: ${toolId}`);
                }

                // Charger les données de l'outil
                const toolPayload = await getTool(toolIdNum);

                if (!toolPayload) {
                    throw new Error(`Tool not found: ${toolId}`);
                }

                setTemplate(toolPayload.template);
                setFilters(toolPayload.filters || []);
                setToolData(toolPayload.data_source?.data_parsed);
                setExpressionContext(toolPayload.expression_context || '');
                setExpressionOnLoad(toolPayload.expression_on_load || '');

            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to load tool data');
            } finally {
                setIsLoading(false);
            }
        };

        loadToolData();
    }, [toolId]);

    if (isLoading) {
        return (
            <div className="w-full bg-blue-100 text-blue-900 py-8 px-4 rounded-lg animate-pulse">
                <div className="flex justify-center items-center">
                    <div className="text-lg font-medium">Chargement de l'outil...</div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="w-full bg-red-100 text-red-900 py-4 px-4 rounded-lg border border-red-300">
                <div className="flex justify-center items-center">
                    <div className="text-sm">
                        <strong>Une erreur est survenue lors de la récupération des données de l'outil.</strong>
                    </div>
                </div>
            </div>
        );
    }

    if (!template) {
        return (
            <div className="w-full bg-yellow-100 text-yellow-900 py-4 px-4 rounded-lg border border-yellow-300">
                <div className="flex justify-center items-center">
                    <div className="text-sm">
                        Outil non disponible
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div id={toolAnchorId(toolId)} className={`mb-8 mt-4 w-full ${TOOL_ANCHOR_CLASS}`}>
            <Tool
                title={title}
                template={template}
                filters={filters}
                toolData={toolData}
                expressionContext={expressionContext}
                expressionOnLoad={expressionOnLoad}
                showFiltersAbove={showFiltersAbove}
                maxFiltersMobile={maxFiltersMobile}
                mobileTemplateSticky={false}
                isPreview={false}
                isEmbedded={true}
            />
        </div>
    );
}
